import type { IPluginRegistryRepository } from '@/domain/repositories/IPluginRegistryRepository';
import { createPluginRegistryEntry } from '@/domain/entities/PluginRegistryEntry';
import type { PluginRegistryEntry } from '@/domain/entities/PluginRegistryEntry';
import type { DeviceSession } from '@/domain/entities/DeviceSession';
import { API_BASE_URL } from '@/infrastructure/http/apiConfig';
import { DeviceSessionExpiredError } from '@/infrastructure/http/DeviceSessionExpiredError';
import { NetworkUnavailableError } from '@/infrastructure/http/NetworkUnavailableError';
import { PluginNotRegisteredError } from '@/infrastructure/http/PluginNotRegisteredError';
import { parseJsonResponse } from '@/infrastructure/http/parseJsonResponse';

function buildPluginRegistryUrl(activationType: string): string {
  return `${API_BASE_URL}/plugin-registry/${encodeURIComponent(activationType)}`;
}

async function fetchPluginRegistry(url: string, jwtToken: string): Promise<Response> {
  try {
    return await fetch(url, { headers: { Authorization: `Bearer ${jwtToken}` } });
  } catch (error) {
    // Mesmo raciocínio de `HttpEventConfigRepository` (achado do Tech Lead,
    // review do PR #85): `fetch()` só rejeita quando a requisição não pôde
    // nem ser tentada — sem conectividade, DNS, CORS bloqueado etc. — nunca
    // um erro genérico na tela (CA-16).
    if (error instanceof TypeError) {
      throw new NetworkUnavailableError();
    }
    throw error;
  }
}

/**
 * Implementação que resolve o registro de plugin por tipo chamando
 * `GET /plugin-registry/{activation_type}` com
 * `Authorization: Bearer <jwtToken>` (HUB-237, ver ADR-010).
 *
 * `require_device_session` no backend também pode rejeitar com 410 (sessão
 * invalidada após a emissão — mesmo mecanismo de `GET /events/:id/config`),
 * mesmo esse endpoint sendo um catálogo global sem escopo por evento.
 */
export class HttpPluginRegistryRepository implements IPluginRegistryRepository {
  async resolve(session: DeviceSession, activationType: string): Promise<PluginRegistryEntry> {
    const response = await fetchPluginRegistry(
      buildPluginRegistryUrl(activationType),
      session.jwtToken
    );

    if (response.status === 410) {
      throw new DeviceSessionExpiredError();
    }

    if (response.status === 404) {
      throw new PluginNotRegisteredError(activationType);
    }

    if (!response.ok) {
      throw new Error(`Falha ao resolver registro de plugin: HTTP ${response.status}`);
    }

    const data = await parseJsonResponse<unknown>(
      response,
      'Não foi possível carregar o plugin de ativação. Tente novamente.'
    );
    return createPluginRegistryEntry(data);
  }
}
